import { useEffect, useRef } from "react";

/**
 * Keeps the active tab (`[data-active="true"]`) horizontally in view within a
 * scroll container whose scrollbar is hidden (`SubHeaderTabs`).
 *
 * The container is `overflow-x-auto scrollbar-hide` inside an `overflow-hidden`
 * parent, so when the available width shrinks — e.g. the analytics copilot side
 * panel opens or is dragged wider — the active tab can slide out of view with no
 * scroll affordance to bring it back. Re-pin it on:
 *  - width changes: `ResizeObserver` (fires on mount + copilot open/close/resize)
 *    plus `window` resize, scrolled instantly so the tab tracks the panel animation
 *  - active-tab changes: `MutationObserver` on `data-active` (click / route navigation),
 *    scrolled smoothly
 *
 * `scrollIntoView({ inline: "nearest" })` is a no-op when the tab is already fully
 * visible, so overlapping triggers are harmless.
 */
export const useKeepActiveTabInView = <
  T extends HTMLElement = HTMLElement,
>() => {
  const navRef = useRef<T>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      return;
    }

    const scrollActiveIntoView = (behavior: ScrollBehavior) => {
      const activeTab = nav.querySelector<HTMLElement>('[data-active="true"]');
      activeTab?.scrollIntoView({
        behavior,
        block: "nearest",
        inline: "nearest",
      });
    };

    const handleResize = () => scrollActiveIntoView("auto");
    window.addEventListener("resize", handleResize);

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(nav);

    const mutationObserver = new MutationObserver(() =>
      scrollActiveIntoView("smooth"),
    );
    mutationObserver.observe(nav, {
      attributes: true,
      attributeFilter: ["data-active"],
      subtree: true,
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return navRef;
};
